/**
 * Google Consent Mode v2 wiring.
 *
 * We ship a tiny inline snippet in <head> that runs BEFORE any Google tag
 * (gtag/GTM) initialises. It sets every advertising + analytics signal to
 * `denied` by default. The visitor's choice (via ConsentBanner /
 * CookieSettings) later triggers `updateGoogleConsent()` which pushes a
 * `consent update` to the shared dataLayer.
 *
 * We DELIBERATELY do NOT read the stored marketing-consent value inside the
 * inline snippet. Consent state lives in localStorage under the Meta
 * consent module and must be applied at boot by `applyStoredGoogleConsent()`
 * from the client bundle — this keeps the SSR-safe inline snippet small,
 * static, and provably fail-closed.
 */
export type GoogleConsentSignal =
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization"
  | "analytics_storage";

export const DEFAULT_DENIED: Record<GoogleConsentSignal, "denied"> = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

/** Inline snippet — must run before any gtag/GTM script tag. */
export function buildConsentDefaultsSnippet(): string {
  return `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});`;
}

type ConsentStatus = "granted" | "denied";

/** Push consent update to the dataLayer. Safe to call any time client-side. */
export function updateGoogleConsent(status: ConsentStatus): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  const payload = {
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
    // Legacy advertising updates must never grant Analytics.
    analytics_storage: "denied",
  };
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", payload);
  } else {
    w.dataLayer.push(["consent", "update", payload]);
  }
}

/** Read stored marketing-consent choice and apply it to Google tags. */
export async function applyStoredGoogleConsent(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const { getConsentState } = await import("@/lib/consent/store");
    updateGoogleConsentCategories(getConsentState());
  } catch {
    /* consent module unavailable — fail closed */
  }
}

/**
 * Per-category update: advertising drives the three ad signals, analytics
 * drives analytics_storage. "unknown" leaves that signal at its denied
 * default until the visitor decides.
 */
export function updateGoogleConsentCategories(state: {
  analytics: "granted" | "denied" | "unknown";
  advertising: "granted" | "denied" | "unknown";
}): void {
  if (typeof window === "undefined") return;
  const ads: ConsentStatus = state.advertising === "granted" ? "granted" : "denied";
  const payload: Record<string, ConsentStatus> = {
    ad_storage: ads, ad_user_data: ads, ad_personalization: ads,
    analytics_storage: state.analytics === "granted" ? "granted" : "denied",
  };
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag === "function") w.gtag("consent", "update", payload);
  else w.dataLayer.push(["consent", "update", payload]);
}
